import React, { useRef, useState, useEffect } from 'react'
import { Rect, Transformer, Text } from 'react-konva'
import { observer } from 'mobx-react-lite'
import { inject } from 'mobx-react'
import { EDITOR_MONITOR_STORE } from 'store'
import { getLayerColor } from '../../../../../../constants/index'
import EntityImage from './EntityImage'
import { RectangleProps } from './types'
import EventIcon from './EventIcon'

const Rectangle = (props: RectangleProps) => {
  const {
    x = 10,
    y = 10,
    width = 50,
    height = 50,
    interactiveAreaId = null,
    eventAtCurrentFrame,
    isSelected,
    overlapOrder,
    zIndex = 0,
    overlappingIds = [],
    onSelect,
    onChange,
    [EDITOR_MONITOR_STORE]: {
      libraryEntityDraggingInfo,
      updateLibraryEntityDraggingInfo,
      assignEntityToIA,
      getColorByIAId
    }
  } = props
  const shapeRef: any = useRef()
  const trRef: any = useRef()

  // Get color based on layer index
  const layerColor = getLayerColor(overlapOrder - 1)

  // Use layerColor (based on order) when overlapping, default color otherwise
  const isOverlapping = overlappingIds.length > 0
  const rectColor = isOverlapping ? layerColor : getColorByIAId(interactiveAreaId)

  const initTransparency = '3a'
  const [colorTransparency, setTransparency] = useState(initTransparency)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!isSelected) return

    trRef.current.setNode(shapeRef.current)
    trRef.current.getLayer().batchDraw()
  }, [isSelected])
  useEffect(() => {
    const node = shapeRef.current
    const canvas = node.getCanvas()

    const offsetX = canvas._canvas.offsetParent.offsetParent.offsetParent.offsetLeft
    const offsetY = canvas._canvas.offsetParent.offsetParent.offsetParent.offsetTop

    const { clientX, clientY, state, entityId, entityClass } = libraryEntityDraggingInfo

    const relativeX = clientX - offsetX
    const relativeY = clientY - offsetY

    const isDraggingOverCurrentRect = relativeX > x && relativeX < x + width &&
      relativeY > y && relativeY < y + height
    // console.log('isDraggingOverCurrentRect', isDraggingOverCurrentRect);
    if (isDraggingOverCurrentRect) {
      if (colorTransparency === initTransparency) {
        setTransparency('94')
      }

      if (state === 'dropped') {
        assignEntityToIA(entityId, entityClass, interactiveAreaId)

        // reset draggingInfo
        updateLibraryEntityDraggingInfo()
      }
    } else if (!isDraggingOverCurrentRect && colorTransparency !== initTransparency) {
      setTransparency(initTransparency)
    }
  }, [libraryEntityDraggingInfo, shapeRef, x, y, width, height, interactiveAreaId,
    colorTransparency, assignEntityToIA, updateLibraryEntityDraggingInfo])

  const onDragEnd = e => {
    dragging && setDragging(false)
    onChange({
      width,
      height,
      x: e.target.x(),
      y: e.target.y()
    })
  }

  const onTransformEnd = () => {
    const node = shapeRef.current
    const scaleX = node.scaleX()
    const scaleY = node.scaleY()

    node.scaleX(1)
    node.scaleY(1)
    onChange({
      x: node.x(),
      y: node.y(),
      width: Math.max(5, node.width() * scaleX),
      height: Math.max(node.height() * scaleY)
    })
  }

  const dragBoundFunc = pos => {
    !dragging && setDragging(true)
    const node = shapeRef.current
    const canvas = node.getCanvas()

    const minX = 0
    const maxX = canvas.width - node.width()
    const minY = 0
    const maxY = canvas.height - node.height()

    return {
      x: Math.min(Math.max(minX, pos.x), maxX),
      y: Math.min(Math.max(minY, pos.y), maxY)
    }
  }

  return (
    <>
      {!dragging &&
        <EntityImage
          key={interactiveAreaId}
          interactiveAreaId={interactiveAreaId}
          x={x} y={y}
        />
      }
      <Rect
        ref={shapeRef}
        draggable
        onClick={onSelect}
        // onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onTransformEnd={onTransformEnd}
        x={x}
        y={y}
        width={width}
        height={height}
        fill={rectColor + colorTransparency}
        stroke={rectColor}
        dragBoundFunc={dragBoundFunc}
      />

      {/* Display box hierarchy number: use overlapOrder when overlapping, order otherwise */}
      {!dragging && interactiveAreaId && isOverlapping && (
        <Text
          x={x + 5}
          y={y + 5}
          text={`${overlapOrder}`}
          // text={`${overlapOrder}\nzIndex=${zIndex}\nIA=${interactiveAreaId}`}
          fontSize={14}
          fontStyle='bold'
          fill='white'
          strokeWidth={0}
          fillWidth={2}
          listening={false}
        />
      )}

      {!dragging &&
        <EventIcon
          x={x} y={y}
          width={width} height={height}
          eventAtCurrentFrame={eventAtCurrentFrame}
        />
      }

      {isSelected &&
        <Transformer
          ref={trRef}
          rotateEnabled={false}
        />
      }
    </>
  )
}

export default inject(EDITOR_MONITOR_STORE)(observer(Rectangle))
